import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as echarts from "echarts";
import { DatePicker, message, Spin, Skeleton } from "antd";
import api from "@/api";
import { useSelectDate } from "@/hooks/useDate";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
dayjs.locale("zh-cn");

import dataJson from "@/../public/data.json";

const prefix = "analysis";
import "@/pages/appDetail/analysis.less";
import { cloneDeep } from "lodash-es";

const { RangePicker } = DatePicker;
const dateFormat = "yyyy-MM-dd";
const rangeDateFormat = "YYYY-MM-DD";

function MyAiModelDistributionBoard() {
  const { resources, two_bars } = dataJson;

  const [active, setActive] = useState<number>(1);
  const [installData, setInstallData] = useState<any>({});
  const [unInstallData, setUnInstallData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [isInstallErr, setIsInstallErr] = useState(true);
  const [isUnInstallErr, setIsUnInstallErr] = useState(true);
  const installChartRef = useRef(null);
  const unInstallChartRef = useRef(null);
  const { selectDate, setSelectDate, disabledDate } = useSelectDate();

  const { id } = useParams();

  const options: any = {
    title: {
      text: "",
      textStyle: {
        color: "#191919",
        fontSize: 16,
      },
    },
    tooltip: {
      trigger: "axis",
    },
    xAxis: {
      type: "category",
      axisTick: {
        show: false,
      },
      data: [],
    },
    yAxis: {
      type: "value",
      splitLine: {
        lineStyle: {
          color: "#EEEEEE",
        },
      },
    },
    grid: {
      left: 46,
      right: 10,
      bottom: 30,
      top: 40,
    },
    series: [
      {
        data: [],
        type: "line",
        symbol: "none",
        smooth: true,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(109,143,240,0.3)" },
            { offset: 1, color: "rgba(0,125,255,0.1)" },
          ]),
        },
        lineStyle: { color: "#6D8FF0" },
        areaStyle: {},
      },
    ],
    graphic: {
      type: "text",
      left: "center",
      top: "middle",
      silent: true,
      invisible: true,
      style: {
        fill: "#9d9d9d",
        fontWeight: "bold",
        text: "暂无数据",
        fontFamily: "Microsoft YaHei",
        fontSize: "25px",
      },
    },
  };

  const handleError = (chartDom: any) => {
    const optionsCopy = cloneDeep(options);
    optionsCopy.graphic.invisible = false;
    const chart: any = chartDom && echarts.init(chartDom);
    chart && chart.setOption(optionsCopy);
  };

  const getInstall = async () => {
    try {
      setLoading(true);
      const res: any = await api.getInstallData({
        timeRangeType: "customize",
        timeRange: {
          startTime: new Date(Number(selectDate[0])).getTime(),
          endTime: new Date(Number(selectDate[1])).getTime(),
        },
        timeFormat: dateFormat,
        appId: id,
      });

      if (res?.code == 200) {
        setInstallData(res.data);
        const optionsCopy = cloneDeep(options);
        optionsCopy.title.text = resources.install_title;
        optionsCopy.xAxis.data = res.data.times;
        optionsCopy.series[0].data = res.data.values || [];
        optionsCopy.graphic.invisible = optionsCopy.series[0].data?.length > 0;
        const installChart: any = installChartRef.current && echarts.init(installChartRef.current);
        installChart && installChart.setOption(optionsCopy);
        setLoading(false);
        setIsInstallErr(false);
      } else {
        setLoading(false);
        setIsInstallErr(true);
        handleError(installChartRef.current);
        message.error(res.msg);
      }
    } catch (errorInfo: any) {
      setLoading(false);
      setIsInstallErr(true);
      handleError(installChartRef.current);
      console.error(errorInfo);
    }
  };
  const getUninstall = async () => {
    try {
      setLoading(true);
      const res: any = await api.getUninstallData({
        timeRangeType: "customize",
        timeRange: {
          startTime: new Date(Number(selectDate[0])).getTime(),
          endTime: new Date(Number(selectDate[1])).getTime(),
        },
        timeFormat: dateFormat,
        appId: id,
      });

      if (res?.code == 200) {
        setUnInstallData(res.data);
        const optionsCopy = cloneDeep(options);
        optionsCopy.title.text = resources.uninstall_title;
        optionsCopy.xAxis.data = res.data.times;
        optionsCopy.series[0].data = res.data.values || [];
        optionsCopy.graphic.invisible = optionsCopy.series[0].data?.length > 0;
        const unInstallChart: any = unInstallChartRef.current && echarts.init(unInstallChartRef.current);
        unInstallChart && unInstallChart.setOption(optionsCopy);
        setLoading(false);
        setIsUnInstallErr(false);
      } else {
        setLoading(false);
        setIsUnInstallErr(true);
        handleError(unInstallChartRef.current);
        message.error(res.msg);
      }
    } catch (errorInfo: any) {
      setLoading(false);
      setIsUnInstallErr(true);
      handleError(unInstallChartRef.current);
      console.error(errorInfo);
    }
  };

  useEffect(() => {
    getInstall();
    getUninstall();
  }, [active, selectDate, id]);

  return (
    <div className={prefix}>
      <Spin spinning={loading} size="large">
        <div className={`${prefix}-top clearfix`}>
          <div
            className={`${prefix}-card ${active === 1 ? "active" : ""}`}
            onClick={() => {
              setActive(1);
            }}
          >
            <Skeleton active loading={isInstallErr}>
              <div className={`${prefix}-card-title`}>{resources.install_title}</div>
              <div className={`${prefix}-card-content`}>
                <div className={`${prefix}-card-num`}>{installData?.totalValue ?? two_bars}</div>
                <div className={`${prefix}-card-content-right`}>
                  <div className={`${prefix}-card-contrastText`}>{resources.contrast}</div>
                  <div className={`${prefix}-card-content-right-bottom`}>
                    <div className={`${prefix}-card-contrastNum`}>
                      {installData?.lastPeriodIsZero
                        ? two_bars
                        : Number(installData?.growthRate) > 0
                        ? `${resources.increase}${Math.abs(Number(installData?.growthRate))}%`
                        : `${resources.reduce}${Math.abs(Number(installData?.growthRate))}%`}
                    </div>
                    {!installData?.lastPeriodIsZero ? (
                      <img
                        src={Number(installData?.growthRate) > 0 ? resources.increase_icon : resources.reduce_icon}
                        alt=""
                        className={`${prefix}-card-icon`}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </Skeleton>
          </div>
          <div
            className={`${prefix}-card ${active === 2 ? "active" : ""}`}
            onClick={() => {
              setActive(2);
            }}
          >
            <Skeleton active loading={isUnInstallErr}>
              <div className={`${prefix}-card-title`}>{resources.uninstall_title}</div>
              <div className={`${prefix}-card-content`}>
                <div className={`${prefix}-card-num`}>{unInstallData?.totalValue ?? two_bars}</div>
                <div className={`${prefix}-card-content-right`}>
                  <div className={`${prefix}-card-contrastText`}>{resources.contrast}</div>
                  <div className={`${prefix}-card-content-right-bottom`}>
                    <div className={`${prefix}-card-contrastNum`}>
                      {unInstallData?.lastPeriodIsZero
                        ? two_bars
                        : Number(unInstallData?.growthRate) > 0
                        ? `${resources.increase}${Math.abs(Number(unInstallData?.growthRate))}%`
                        : `${resources.reduce}${Math.abs(Number(unInstallData?.growthRate))}%`}
                    </div>
                    {!unInstallData?.lastPeriodIsZero ? (
                      <img
                        src={Number(unInstallData?.growthRate) > 0 ? resources.increase_icon : resources.reduce_icon}
                        alt=""
                        className={`${prefix}-card-icon`}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </Skeleton>
          </div>
          <RangePicker
            value={selectDate}
            allowClear={false}
            disabledDate={disabledDate}
            format={rangeDateFormat}
            className={`${prefix}-rangePicker`}
            onChange={(val: any) => {
              setSelectDate(val === null ? [] : val);
            }}
          />
        </div>
        {active === 1 ? (
          <div ref={installChartRef} className={`${prefix}-chart`} />
        ) : (
          <div ref={unInstallChartRef} className={`${prefix}-chart`} />
        )}
      </Spin>
    </div>
  );
}

export default MyAiModelDistributionBoard;
